import React, {useContext, useEffect, useState} from 'react';
import JobListViewContext, { RightClickContext } from './JobListViewContext';
import { JobNames } from '../../framework.core/JobNames';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../ui/ActionButton';
import ToolboxMenuItem from '../../uiControls/ToolboxMenuItem';
import ListViewShortcutMenu from './ListViewShortcutMenu';
import useFunctionKeys from '../../hooks/useFunctionKeys';
import AbstractListView, { DataFetchResult, ExportRowRenderer, ListCustomFilterRenderer, ListOrderBy, ListRowRenderer } from './AbstractListView';
import { Search } from '../../filtering';
import constants from '../../constants';
import { DateRenderer, DateTimeRenderer, FloatRenderer, NullableCheckboxIconRenderer, TextAlignLeftRenderer, TextRenderer } from '../../uiControls/SmartTableRenderers';
import { reportHistoryList, TableColumn, TableSettings } from '../../tableSettings';
import JobListSuntekCommand from './JobListSuntekCommand';
import * as XCol from '../../framework.core/XExportColumn';
import Menu, { IfmsMenuItem } from '../../ui/menu/Menu';
import { ReportHistoryListItem } from '../../fms.data/models/ReportHistoryListItem';
import ApplicationContext from '../../ApplicationContext';
import config from '../../config';

function ResultRenderer({ cell, row, data }: any) {
  const l = data[row.index];
  const sentResult = l.sentResult;

  return <div className="ifms-table-align-center" id={`ifms-table-default-${row.index}`}>{cell.value}</div>;
}

async function openLogFile(logFileId: number, logFileName: string, userToken: string) {
  if (logFileId <= 0 || !logFileName) return;
  try {
    const response = await fetch(`${config.serverUrl}memo/openLogFile?logFileId=${logFileId}&logFileName=${logFileName}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${userToken}`
      }
    });

    if (response.ok) {

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = logFileName;
        a.click();
      
    } else {
      console.error('error', response.status);
    }
  } catch (error) {
    console.error('error', error);
  }
}

function sanitizeFileName(fileName: string) {
    return fileName
      ?.replaceAll(/\:/g, '-')                // 윈도우 파일시스템 제한 문자
      ?.replaceAll(/[/\\?%*|"<>+]/g, ' ')     // 윈도우 파일시스템 제한 문자 && 업로드 파일 URL 호출 시 특수문자가 포함되어 있을 경우 에러가 발생함("+" 문자 추가)
      ?.replaceAll(/\./g, ' ')                // 마침표를 공백으로            ???
      ?.replaceAll(/\#/g, ' ')                // #을 공백으로                ???
      ?.replaceAll(/\s+/g, ' ')               // 연속된 공백을 하나로
      ?.trim();                               // 앞뒤 공백 제거
}

function FileRenderer({cell, row, data}: any){
  const l = data [row.index];
  const fileId = l.fileId;
  const appContext = useContext(ApplicationContext);

  function DownLoadFile(){
    const logFileId = fileId;
    const fileName = sanitizeFileName(l.reportName);
    const logFileName = (`${fileName}.xlsx`).replaceAll(' ', '-');
    const userToken = appContext.user ? appContext.user.accessToken ?? '' : '';

    openLogFile(logFileId, logFileName, userToken);
  }
  
  if(fileId > 0)   return <div className="ifms-table-align-center" id={`ifms-table-default-${row.index}`} style={{fontWeight: 'bold', color:'blue', cursor:'pointer' }} onClick={DownLoadFile} >View</div>;
  else return <div></div>;
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const jobListContext = useContext(JobListViewContext);

  const items = [
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: 'Refresh', shortcut: 'F11', separator: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        jobListContext.triggerTableSearch('F5');
        break;
      case 1:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  return (
    <></>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F5") {
      jobListContext.triggerTableSearch('F5');
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F5', 'F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    null
  );
}

/**
 *
 * @class ReportHistoryListView
 * @extends {AbstractListView}
 */
class ReportHistoryListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.ReportHistory,
      'ReportHistoryListItem',
      ReportHistoryListItem.calculateSearchParameters(search,ReportHistoryListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: ReportHistoryListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.ReportHistory, 'ReportHistoryListItem',
      ReportHistoryListItem.calculateSearchParameters(search, ReportHistoryListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  ReportHistoryListItem.formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return ReportHistoryListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.dataIndex === 'sentResult' ) colRenderer.Cell = ResultRenderer;
      else if(x.dataIndex === 'fileId') colRenderer.Cell = FileRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = (props:any) => <FloatRenderer {...props} decimals={2} />;
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'Id',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  reportHistoryList;
    const cols = ReportHistoryListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  
  hasMemo(): boolean {
    return false;
  }
  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: ReportHistoryListItem ) {
    return '';
  }



  getListViewTitle() {
    return 'Report History';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.ReportHistory} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
}

export default ReportHistoryListView;